/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { leaderApi } from '../../../api/leaderApi';
import { toLocalDateStr, getAssignmentConflicts } from '../../../utils/leaderUtils';
import { formatAssignmentDateSpan, isMultiDay } from '../../../utils/dateUtils';
import AssignmentHoverCard from './AssignmentHoverCard';

/**
 * ContextCalendar Component
 * Shows overlapping assignments for a specific time range to help resolve conflicts.
 * Now groups conflict-free days into a single row to save space.
 */
export default function ContextCalendar({ 
    startDate, 
    endDate, 
    currentStartTime,
    currentEndTime,
    isAllDay,
    currentAssignmentId, 
    onAssignmentClick,
    onAssignmentHover,
    onAssignmentHoverEnd,
    canGoBack,
    canGoForward,
    onGoBack,
    onGoForward,
    filterByWorkerId,
    // Props for hover card / conflict detection
    workers = [],
    vehicles = [],
    assignmentsForConflicts = [],
    absencesForConflicts = [],
    kolonnen = [],
    isConflictDetectionEnabled = true,
    isUndated = false,
    hideNavigation = false
}) {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const hoverTimeoutRef = useRef(null);
    const panelRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const [isAtEnd, setIsAtEnd] = useState(true);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        };
    }, []);

    // Load Context Assignments
    useEffect(() => {
        if (!startDate || !endDate) return;

        const loadContext = async () => {
            setLoading(true);
            try {
                const start = new Date(startDate);
                start.setDate(start.getDate() - 1);
                const end = new Date(endDate);
                end.setDate(end.getDate() + 1);

                const data = await leaderApi.getAssignments({
                    start: toLocalDateStr(start),
                    end: toLocalDateStr(end)
                });
                
                let filteredData = data || [];
                
                if (filterByWorkerId) {
                    filteredData = filteredData.filter(a => 
                        a.workers && a.workers.some(w => String(w.worker_id) === String(filterByWorkerId))
                    );
                }

                setAssignments(filteredData);
            } catch (err) {
                console.error('Error loading context calendar:', err);
            } finally {
                setLoading(false);
            }
        };

        loadContext();
    }, [startDate, endDate, filterByWorkerId]);

    // Scroll State Synchronization
    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;

        const syncScrollState = () => {
            const scrollable = panel.scrollHeight > panel.clientHeight + 5;
            const atEnd = !scrollable || panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 10;
            setIsScrollable(scrollable);
            setIsAtEnd(atEnd);
        };

        syncScrollState();
        const timer = setTimeout(syncScrollState, 150);

        panel.addEventListener('scroll', syncScrollState, { passive: true });
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncScrollState) : null;
        resizeObserver?.observe(panel);

        return () => {
            clearTimeout(timer);
            panel.removeEventListener('scroll', syncScrollState);
            resizeObserver?.disconnect();
        };
    }, [assignments, loading, startDate, endDate]);

    const days = useMemo(() => {
        const daysArr = [];
        let curr = new Date(startDate);
        const last = new Date(endDate);
        
        let count = 0;
        // Limit of 31 days for performance safety
        while (curr <= last && count < 31) {
            daysArr.push(new Date(curr));
            curr.setDate(curr.getDate() + 1);
            count++;
        }
        return daysArr;
    }, [startDate, endDate]);

    // Intelligent Grouping: consecutive conflict-free days are combined
    const dayGroups = useMemo(() => {
        if (!days.length) return [];
        const groups = [];
        let currentEmptyGroup = null;

        days.forEach(day => {
            const dateStr = toLocalDateStr(day);
            const dayAssignments = assignments.filter(a => {
                if (currentAssignmentId && String(a.id) === String(currentAssignmentId)) return false;
                const aStart = toLocalDateStr(new Date(a.start_datetime));
                const aEnd = toLocalDateStr(new Date(a.end_datetime));
                return dateStr >= aStart && dateStr <= aEnd;
            });

            if (dayAssignments.length === 0) {
                if (!currentEmptyGroup) {
                    currentEmptyGroup = { type: 'empty-range', days: [day] };
                    groups.push(currentEmptyGroup);
                } else {
                    currentEmptyGroup.days.push(day);
                }
            } else {
                currentEmptyGroup = null;
                groups.push({ type: 'day-with-entries', day, dayAssignments });
            }
        });
        return groups;
    }, [days, assignments, currentAssignmentId]);

    const handleMouseEnter = (e, a) => {
        onAssignmentHover?.(a, e);
    };

    const handleMouseLeave = () => {
        onAssignmentHoverEnd?.();
    };

    const renderAssignmentList = (list) => list.map(a => {
        const status = (a.status || 'geplant').toLowerCase();
        const startTime = a.start_datetime ? new Date(a.start_datetime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
        const endTime = a.end_datetime ? new Date(a.end_datetime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';

        return (
            <div 
                key={a.id} 
                className={`cc-slot status-${status}`}
                onClick={() => onAssignmentClick?.(a)}
                onMouseEnter={(e) => handleMouseEnter(e, a)}
                onMouseLeave={handleMouseLeave}
            >
                <div className="cc-slot-content">
                    <div className="cc-slot-title">
                        {a.project_name}
                    </div>
                    <div className="cc-slot-time">
                        <span>{a.all_day && isMultiDay(a) ? formatAssignmentDateSpan(a) : (a.all_day ? 'Ganztag' : `${startTime} - ${endTime}`)}</span>
                        <div className="cc-slot-stats">
                            {a.workers?.length > 0 && <span>👤 {a.workers.length}</span>}
                            {a.vehicles?.length > 0 && <span>🚚 {a.vehicles.length}</span>}
                        </div>
                    </div>
                </div>
            </div>
        );
    });

    return (
        <div className="context-calendar">
            <style>{`
                @keyframes ccBounce {
                    0%, 20%, 50%, 80%, 100% {transform: translateX(-50%) translateY(0);}
                    40% {transform: translateX(-50%) translateY(-5px);}
                    60% {transform: translateX(-50%) translateY(-3px);}
                }
            `}</style>
            
            <div className="cc-header">
                <div className="cc-header-main">
                    <span className="cc-title">📅 Kontext-Kalender</span>
                    {!hideNavigation && (
                        <div className="cc-nav-group">
                            <button 
                                className="cc-nav-btn" 
                                onClick={onGoBack} 
                                disabled={!canGoBack}
                                title="Zurück zum vorherigen Eintrag"
                            >
                                ←
                            </button>
                            <button 
                                className="cc-nav-btn" 
                                onClick={onGoForward} 
                                disabled={!canGoForward}
                                title="Vor zum nächsten Eintrag"
                            >
                                →
                            </button>
                        </div>
                    )}
                </div>
                <span className="cc-range">
                    {startDate === endDate ? (
                        startDate?.split('-').reverse().join('.')
                    ) : (
                        `${startDate?.split('-').reverse().join('.')} - ${endDate?.split('-').reverse().join('.')}`
                    )}
                </span>
            </div>

            <div className="cc-body-wrapper" style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <div className="cc-body" ref={panelRef} style={{ flex: 1, overflowY: 'auto' }}>
                    {isUndated ? (
                        <div className="cc-undated-info" style={{ 
                            padding: '40px 20px', 
                            textAlign: 'center', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: '12px',
                            color: '#64748b'
                        }}>
                            <div style={{ fontSize: '2rem', opacity: 0.5 }}>📅</div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Projekt ist undatiert</div>
                            <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                Es liegen keine Zeitdaten vor. <br/>
                                Weisen Sie ein Datum zu, um Überschneidungen zu prüfen.
                            </div>
                        </div>
                    ) : loading ? (
                        <div className="cc-loading">Lädt...</div>
                    ) : (
                        <div className="cc-timeline">
                            {dayGroups.map((group, idx) => {
                                if (group.type === 'empty-range') {
                                    const first = group.days[0];
                                    const last = group.days[group.days.length - 1];
                                    const isSingle = group.days.length === 1;

                                    return (
                                        <div key={`empty-${idx}`} className="cc-range-row empty">
                                            <div className="cc-range-label">
                                                {isSingle ? (
                                                    <span>{first.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
                                                ) : (
                                                    <span>
                                                        {first.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })} - {last.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="cc-range-status">Keine Konflikte</div>
                                        </div>
                                    );
                                }

                                const { day, dayAssignments } = group;
                                const dateStr = toLocalDateStr(day);

                                return (
                                    <div key={dateStr} className="cc-day-row">
                                        <div className="cc-day-label">
                                            <span className="cc-day-name">{day.toLocaleDateString('de-DE', { weekday: 'short' })}</span>
                                            <span className="cc-day-date">{day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                                        </div>
                                        <div className="cc-groups-wrapper">
                                            {(() => {
                                                const subGroups = { before: [], overlap: [], after: [] };
                                                
                                                dayAssignments.forEach(a => {
                                                    let dayActiveStart = '00:00';
                                                    let dayActiveEnd = '23:59';

                                                    if (!isAllDay) {
                                                        if (dateStr === startDate) dayActiveStart = currentStartTime;
                                                        if (dateStr === endDate) dayActiveEnd = currentEndTime;
                                                    }

                                                    if (a.all_day) {
                                                        subGroups.overlap.push(a);
                                                        return;
                                                    }

                                                    const aStart = (a.start_datetime ? a.start_datetime.split(/[T ]/)[1]?.substring(0, 5) : '00:00') || '00:00';
                                                    const aEnd = (a.end_datetime ? a.end_datetime.split(/[T ]/)[1]?.substring(0, 5) : '23:59') || '23:59';
                                                    
                                                    if (aEnd <= dayActiveStart) subGroups.before.push(a);
                                                    else if (aStart >= dayActiveEnd) subGroups.after.push(a);
                                                    else subGroups.overlap.push(a);
                                                });

                                                return (
                                                    <>
                                                        {subGroups.before.length > 0 && <div className="cc-group">
                                                            <div className="cc-group-label">Vorher</div>
                                                            {renderAssignmentList(subGroups.before)}
                                                        </div>}
                                                        {subGroups.overlap.length > 0 && <div className="cc-group">
                                                            <div className="cc-group-label">Überschneidung</div>
                                                            {renderAssignmentList(subGroups.overlap)}
                                                        </div>}
                                                        {subGroups.after.length > 0 && <div className="cc-group">
                                                            <div className="cc-group-label">Nachher</div>
                                                            {renderAssignmentList(subGroups.after)}
                                                        </div>}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {isScrollable && !isAtEnd && (
                    <div className="cc-scroll-hint" style={{
                        position: 'absolute',
                        bottom: '12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#007aff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 20,
                        border: '1px solid rgba(0,122,255,0.1)',
                        pointerEvents: 'none',
                        animation: 'ccBounce 2s infinite'
                    }}>
                        ↓ Weiter scrollen
                    </div>
                )}
            </div>
            
            <div className="cc-footer">
                <p className="cc-hint">Zeigt Überschneidungen im gewählten Zeitraum.</p>
            </div>
        </div>
    );
}
